import { useEffect, useState } from 'react'
import {
  Button,
  FlexCell,
  FlexRow,
  FlexSpacer,
  LabeledInput,
  PickerInput,
  TextArea,
  Rating,
  useForm,
} from '@epam/loveship'
import { useArrayDataSource, useUuiContext, UuiContexts } from '@epam/uui-core'
import {
  useGetLayersQuery,
  useGetReviewQuery,
  usePutPointMutation,
} from '../../../../store'
import { SidebarPanelHeader } from '../../../../components/SidebarPanel/components/SidebarPanelHeader'
import { SidebarPanelMain } from '../../../../components/SidebarPanel/components/SidebarPanelMain'
import { LayerResponse, PointResponse } from '../../../../constants/types'
import { MAX_CHAR_LIMIT } from '../../../../constants/values'
import { useAppSelector } from '../../../../hooks/redux/redux'
import { useActions } from '../../../../hooks/redux/actions'
import { useSideBarControlls } from '../../../../hooks/useSideBarControlls'
import { useFetchLocationAddress } from '../../../../hooks/useFetchLocationAddress'
import { showLeavePointEditingModal } from '../../../../utils/modals/showLeavePointEditingModal'
import { showErrorNotification } from '../../../../utils/notifications/showErrorNotification'
import { showSuccessNotification } from '../../../../utils/notifications/showSuccessNotification'
import css from '../EditReviewForm/EditReview.module.scss'

interface EditPoint {
  pointName: string
  pointDescription: string
  pointCategory: number | null
  pointLocation: string | null
  itemReview: string | null
  itemRating: number | null
}

type EditPointFormProps = {
  point: PointResponse | null
}

export const EditPointForm = ({ point }: EditPointFormProps) => {
  const { closeEditPoint } = useSideBarControlls()
  const { data: layers } = useGetLayersQuery()
  const svc = useUuiContext<UuiContexts>()
  const [updatePoint] = usePutPointMutation()
  const { updateCurrentPoint, setSelectedPoint, setSideBarClosingInitiated } =
    useActions()
  const { isSideBarCloseInitiated } = useAppSelector((state) => state.app)
  const { editPointPosition } = useAppSelector((state) => state.points)

  const address = useFetchLocationAddress(editPointPosition)
  const { data: review } = useGetReviewQuery(point?.id)

  useEffect(() => {
    if (isSideBarCloseInitiated) onCloseEditPointForm()
  }, [isSideBarCloseInitiated])

  const onCloseEditPointForm = () => {
    setSideBarClosingInitiated(false)
    showLeavePointEditingModal(svc).then(() => {
      closeEditPoint(false)
    })
  }

  const { lens, save } = useForm<EditPoint>({
    value: {
      pointName: point?.name || '',
      pointDescription: point?.description || '',
      pointCategory: point?.layerId || null,
      pointLocation: address.location ?? point?.location,
      itemReview: review?.reviewText ?? '',
      itemRating: review?.rating ?? 0,
    },
    // Passed null to prevent show "Save Changes?" dialog after history.push
    // Expected behaviour with null: allways saves changes when unmount
    beforeLeave: null,
    onSave: (newPoint) => {
      const bodyNewPoint = {
        ...point,
        lat: editPointPosition?.lat ?? point?.lat,
        lng: editPointPosition?.lng ?? point?.lng,
        layerId: newPoint.pointCategory,
        name: newPoint.pointName,
        description: newPoint.pointDescription,
        location: address.location ?? newPoint.pointLocation,
        countryCode: address.countryCode,
        rating: newPoint.itemRating,
        review: newPoint.itemReview,
      }
      // setEditPointPosition(null)
      return updatePoint(bodyNewPoint)
        .unwrap()
        .then((value) => {
          if (value) {
            updateCurrentPoint(value as PointResponse)
            setSelectedPoint(value as PointResponse)
            //TODO: Refactor setSelectedPoint
          }
          return value
        })
    },
    onSuccess: (result) => {
      closeEditPoint()
      showSuccessNotification({
        svc,
        message: 'Place was successfully updated',
      })
    },
    onError: (error) => {
      const errorMessage =
        error.status === 422 ? error.data.name : error.message
      showErrorNotification({
        svc,
        message: `Error on save: ${errorMessage}`,
      })
    },
    getMetadata: () => ({
      props: {
        pointName: { isRequired: true },
        pointCategory: { isRequired: true },
        pointDescription: { isRequired: true },
      },
    }),
  })

  const [pointDescriptionValue, setPointDescriptionValue] = useState(
    lens.prop('pointDescription').get()
  )
  const [pointNameValue, setPointNameValue] = useState(
    lens.prop('pointName').get()
  )
  const [itemReviewValue, setItemReviewValue] = useState(
    lens.prop('itemReview').get()
  )
  const ratingFetched = lens.prop('itemRating').get()
  const [itemRating, setItemRating] = useState(0)
  useEffect(() => {
    if (itemRating === 0 && ratingFetched > 0) {
      setItemRating(ratingFetched)
    }
  }, [ratingFetched, itemRating])
  const [pointCategory, setPointCategory] = useState(
    lens.prop('pointCategory').get()
  )

  const onSave = () => {
    lens.prop('pointDescription').set(pointDescriptionValue)
    lens.prop('pointName').set(pointNameValue)
    lens.prop('pointCategory').set(pointCategory)
    lens.prop('itemReview').set(itemReviewValue)
    lens.prop('itemRating').set(itemRating)
    save()
  }

  const layersDataSource = useArrayDataSource(
    { items: layers || ([] as LayerResponse[]) },
    []
  )

  const isMaxLimitReached =
    lens.prop('pointDescription').toProps().value.length >=
    MAX_CHAR_LIMIT.DESCRIPTION

  return (
    <>
      <SidebarPanelHeader title="Edit Place" />

      <SidebarPanelMain>
        <FlexRow vPadding="12" alignItems="top">
          <FlexCell grow={1}>
            <LabeledInput
              label="Place Category"
              {...lens.prop('pointCategory').toProps()}
            >
              <PickerInput
                value={pointCategory}
                onValueChange={setPointCategory}
                dataSource={layersDataSource}
                minBodyWidth={4}
                getRowOptions={(item) => ({
                  isSelectable: !!item?.parentId,
                })}
                getName={(item) => item?.name || 'n/a'}
                placeholder={'Select place category'}
                selectionMode="single"
                valueType="id"
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput
              label="Place Name"
              {...lens.prop('pointName').toProps()}
            >
              <TextArea
                value={pointNameValue}
                onValueChange={setPointNameValue}
                autoSize={true}
                maxLength={MAX_CHAR_LIMIT.NAME}
                placeholder="Place Name"
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput
              label="Place Description"
              {...lens.prop('pointDescription').toProps()}
            >
              <TextArea
                value={pointDescriptionValue}
                onValueChange={setPointDescriptionValue}
                cx={isMaxLimitReached ? 'textAreaLimitReached' : ''}
                maxLength={MAX_CHAR_LIMIT.DESCRIPTION}
                autoSize
                rows={2}
                placeholder="Place Description"
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput
              label="Place Location"
              {...lens.prop('pointLocation').toProps()}
            >
              <TextArea
                cx={isMaxLimitReached ? 'textAreaLimitReached' : ''}
                maxLength={MAX_CHAR_LIMIT.DESCRIPTION}
                autoSize
                rows={2}
                placeholder="Place Location"
                {...lens.prop('pointLocation').toProps()}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexCell width="auto">
            <LabeledInput
              label="Change Rating"
              {...lens.prop('itemRating').toProps()}
            >
              {/*Rating field workaround for UUI issue https://github.com/epam/UUI/issues/1168*/}
              <Rating
                value={itemRating}
                onValueChange={setItemRating}
                isInvalid={false}
                from={1}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput
              label="Change Review"
              {...lens.prop('itemReview').toProps()}
            >
              <TextArea
                value={itemReviewValue}
                onValueChange={setItemReviewValue}
                cx={isMaxLimitReached ? 'textAreaLimitReached' : ''}
                autoSize
                rows={2}
                placeholder="Place Review"
                maxLength={MAX_CHAR_LIMIT.REVIEW}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexSpacer />
          <Button
            caption="Cancel"
            onClick={onCloseEditPointForm}
            color="sky"
            fill="white"
          />
          <Button
            caption="Save"
            onClick={onSave}
            color="sky"
            cx={css.marginBtn}
          />
        </FlexRow>
      </SidebarPanelMain>
    </>
  )
}
